import type {QueryClient, QueryFilters} from '@tanstack/react-query'

import type {PaginatedGroupsAndSecondaryGroupsData, PaginatedGroupsData} from '../../../api/memex-items/paginated-views'
import {isPageParamsDataGrouped, isPageParamsDataGroupedWithSecondaryGroups} from '../queries/query-data-helpers'
import {
  buildPageParamsQueryKey,
  buildSubIssuesPageParamsQueryKey,
  createGroupedItemsId,
  getPageTypeFromQueryKey,
  isInitialQueryKeyForView,
  isPageTypeForGroupedItems,
  isQueryForItemsMetadata,
  isQueryKeyForGroupedItems,
  type MemexGroupedItemsQueryKey,
  type PageTypeForGroupedItems,
  pageTypeForGroups,
  pageTypeForSecondaryGroups,
  type PaginatedMemexItemsQueryKey,
  paginatedMemexItemsQueryKey,
  subIssuesQueryKey,
} from '../queries/query-keys'
import {isGroupByApplied, isGroupByAppliedInBothDirections} from '../queries/query-variables'
import {
  type GroupedPageParamsQueryData,
  type GroupedWithSecondaryGroupsPageParamsQueryData,
  type GroupedWithSubIssuesPageParamsQueryData,
  type MemexGroupsPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  pageParamForNextPlaceholder,
  type PageParamsByGroupedItemsId,
  type PageParamsQueryData,
  type PaginatedMemexItemsQueryVariables,
  type SubIssuesPageParams,
  type UngroupedPageParamsQueryData,
  type UngroupedWithSubIssuesPageParamsQueryData,
  type WithoutTotal,
} from '../queries/types'

/**
 * A thin wrapper around `getQueryData`, that returns the page params for which query keys should be
 * active in our view
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables to use in our query key
 * @returns PageParamsQueryData
 */
export function getPageParamsQueryDataForVariables(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
) {
  return (
    queryClient.getQueryData<PageParamsQueryData>(buildPageParamsQueryKey(variables)) ||
    buildEmptyPageParamsQueryData(queryClient, variables)
  )
}

/**
 * A thin wrapper around `getQueryData`, that returns the page params
 * for sub-issues in the view
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables to use to build a query key -
 * sub-issues query keys use a subset of these variables.
 * @returns SubIssuesPageParams
 */
export function getSubIssuesPageParamsQueryDataForVariables(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
): SubIssuesPageParams | Record<never, never> {
  if (variables.hierarchy?.enabled) {
    const parentIssues = queryClient.getQueryData<SubIssuesPageParams>(buildSubIssuesPageParamsQueryKey(variables))
    return parentIssues || {parentIssues: {}}
  }
  return {}
}

/**
 * A thin wrapper around `setQueryData`, that sets the page params for which query keys should be
 * active in our view
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables that is used in our queryKey
 * @param newQueryData
 */
export function setPageParamsQueryDataForVariables(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  newQueryData: PageParamsQueryData,
) {
  if ('parentIssues' in newQueryData) {
    queryClient.setQueryData<SubIssuesPageParams>(buildSubIssuesPageParamsQueryKey(variables), {
      parentIssues: newQueryData.parentIssues,
    })
  }
  queryClient.setQueryData<PageParamsQueryData>(buildPageParamsQueryKey(variables), newQueryData)
}

/**
 * Removes inactive queryData except the first page for each view.
 * Invalidates that first page so that it will be refetched
 * the next time the view becomes active.
 * @param queryClient
 */
export const invalidateInactiveInitialQueries = (queryClient: QueryClient) => {
  const baseInactiveMemexItemsQueryFilter: QueryFilters = {
    queryKey: ['memex', paginatedMemexItemsQueryKey],
    exact: false,
    type: 'inactive',
  }
  // When a user returns to a view, their scroll position will be reset to the top.
  // So we don't need to keep subsequent pages of data.
  queryClient.removeQueries({
    ...baseInactiveMemexItemsQueryFilter,
    predicate: query => {
      if (isQueryForItemsMetadata(query)) {
        return false
      }
      const queryKey = query.queryKey as PaginatedMemexItemsQueryKey
      if (queryKey[4] === pageParamForInitialPage && isQueryKeyForGroupedItems(queryKey)) {
        // Remove initial page of groupedItems that aren't in the first top-level page of groups
        return !isGroupedItemsQueryInInitialPageOfGroups(queryClient, queryKey)
      }
      if (isInitialQueryKeyForView(queryKey)) {
        resetPageParamsQueryDataToInitialPage(queryClient, queryKey[2])
      }
      return queryKey[4] !== pageParamForInitialPage && queryKey[4] !== pageParamForNextPlaceholder
    },
  })
  // We want to invalidate the first page of data so that it will be refetched
  // when the view becomes active.
  queryClient.invalidateQueries({
    ...baseInactiveMemexItemsQueryFilter,
    refetchType: 'active',
    predicate: query => {
      const queryKey = query.queryKey as PaginatedMemexItemsQueryKey
      return !isQueryForItemsMetadata(query) && isInitialQueryKeyForView(queryKey)
    },
  })
}

/**
 * Invalidate all active and inactive sub-issues queries.
 * Active queries will be refetched immediately, while inactive queries will be marked as "stale"
 * and will be refetched the next time the view becomes active.
 * @param queryClient
 */
export const invalidateSubIssuesQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: ['memex', subIssuesQueryKey],
    exact: false,
    refetchType: 'active',
  })
}

/**
 * Resets each page params query to only include the initial page param.
 * @param queryClient
 * @param variables The variables used in the page params query key
 */
function resetPageParamsQueryDataToInitialPage(queryClient: QueryClient, variables: PaginatedMemexItemsQueryVariables) {
  const pageParamsQueryData = getPageParamsQueryDataForVariables(queryClient, variables)
  if (!pageParamsQueryData || pageParamsQueryData.pageParams.length === 0) return
  if (pageParamsQueryData.pageParams[pageParamsQueryData.pageParams.length - 1] === pageParamForNextPlaceholder) {
    // Skip any page params queries that are tracking placeholder data
    return
  }
  // Reset the top-level page params for the view to just the initial page.
  // Also reset any sub-issues page params.
  let newPageParams: PageParamsQueryData = {
    pageParams: [pageParamForInitialPage],
    ...getSubIssuesPageParamsQueryDataForVariables(queryClient, variables),
  }

  if (isPageParamsDataGrouped(pageParamsQueryData)) {
    // If the data is grouped, we should also reset the groupedItems page params
    // to just those in the initial page for the view.
    const groupedItemsPageTypes = buildGroupedItemsPageTypesForInitialPage(queryClient, variables)
    newPageParams = {
      ...newPageParams,
      groupedItems: groupedItemsPageTypes.reduce((acc, pageType) => {
        const groupedItemsId = createGroupedItemsId(pageType)
        acc[groupedItemsId] = [pageParamForInitialPage]
        return acc
      }, {} as PageParamsByGroupedItemsId),
    }
  }
  if (isPageParamsDataGroupedWithSecondaryGroups(pageParamsQueryData)) {
    // if the data is grouped with secondary groups, we should also reset the
    // secondary groups page params for the view to just the initial page
    newPageParams = {
      ...newPageParams,
      secondaryGroups: [pageParamForInitialPage],
      // groupedItemBatches are always for subsequent pages, so we should reset
      // these page params entirely
      groupedItemBatches: [],
    }
  }
  setPageParamsQueryDataForVariables(queryClient, variables, newPageParams)
}

/**
 * Checks if a queryKey is for a groupedItems collection in the initial
 * page of groups returned by the server
 * @param queryClient
 * @param groupQueryKey The queryKey for the groupedItems to verify
 * @returns boolean
 */
function isGroupedItemsQueryInInitialPageOfGroups(queryClient: QueryClient, groupQueryKey: MemexGroupedItemsQueryKey) {
  const groupedItemsPageTypes = buildGroupedItemsPageTypesForInitialPage(queryClient, groupQueryKey[2])
  const groupIdsInInitialPage = groupedItemsPageTypes.map(pageType => createGroupedItemsId(pageType))
  const pageType = getPageTypeFromQueryKey(groupQueryKey)
  const groupedItemsId = isPageTypeForGroupedItems(pageType) ? createGroupedItemsId(pageType) : ''
  return groupIdsInInitialPage.includes(groupedItemsId)
}

/* Builds groupedItems PageTypes for the initial page of groups
 * or initial intersection of groups and secondary groups
 */
const buildGroupedItemsPageTypesForInitialPage = (
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
): Array<PageTypeForGroupedItems> => {
  const initialPageOfGroups = queryClient.getQueryData<MemexGroupsPageQueryData>([
    'memex',
    paginatedMemexItemsQueryKey,
    variables,
    pageTypeForGroups,
    pageParamForInitialPage,
  ])
  if (!initialPageOfGroups) {
    return []
  }
  const initialPageOfSecondaryGroups = queryClient.getQueryData<MemexGroupsPageQueryData>([
    'memex',
    paginatedMemexItemsQueryKey,
    variables,
    pageTypeForSecondaryGroups,
    pageParamForInitialPage,
  ])
  if (!initialPageOfSecondaryGroups) {
    return initialPageOfGroups.groups.map(g => ({groupId: g.groupId}))
  }
  return initialPageOfGroups.groups.reduce((acc, g) => {
    for (const sg of initialPageOfSecondaryGroups.groups) {
      acc.push({groupId: g.groupId, secondaryGroupId: sg.groupId})
    }
    return acc
  }, [] as Array<PageTypeForGroupedItems>)
}

// The next_placeholder should always be the last page param, so hasNextPage must be false.
export const nextPlaceholderPageInfo = {hasNextPage: false, hasPreviousPage: false}

export function buildEmptyPageParamsQueryData(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
): PageParamsQueryData {
  // All views start with a single page param which triggers a single request
  // for the initial page of data.
  const initialPageParamsForView: UngroupedPageParamsQueryData = {
    pageParams: [pageParamForInitialPage],
  }
  if (isGroupByAppliedInBothDirections(variables)) {
    // When the view is grouped in both directions, initialize empty
    // groupedItems, secondaryGroups, and groupedItemBatches objects.
    // These will be populated using the initial server response.
    const pageParams: GroupedWithSecondaryGroupsPageParamsQueryData = {
      ...initialPageParamsForView,
      groupedItems: {},
      secondaryGroups: [],
      groupedItemBatches: [],
    }
    return pageParams
  } else if (isGroupByApplied(variables)) {
    // When the view is grouped, we should initialize the groupedItems object.
    // This will be populated using the initial server response.
    // Additionally check if hierarchy is enabled.
    const pageParams: GroupedPageParamsQueryData | GroupedWithSubIssuesPageParamsQueryData = {
      ...initialPageParamsForView,
      groupedItems: {},
      ...getSubIssuesPageParamsQueryDataForVariables(queryClient, variables),
    }
    return pageParams
  } else {
    // When view is ungrouped, additionally check if hierarchy is enabled.
    // If not, no other initialization is needed.
    const pageParams: UngroupedPageParamsQueryData | UngroupedWithSubIssuesPageParamsQueryData = {
      ...initialPageParamsForView,
      ...getSubIssuesPageParamsQueryDataForVariables(queryClient, variables),
    }
    return pageParams
  }
}

export const emptyPageParamsForGroupItems = (
  response: WithoutTotal<PaginatedGroupsData> | WithoutTotal<PaginatedGroupsAndSecondaryGroupsData>,
): Record<string, Array<PageParam>> => {
  const emptyPageParamsByGroupId: Record<string, Array<PageParam>> = {}
  const secondaryGroups = response.secondaryGroups
  if (!secondaryGroups) return emptyPageParamsByGroupId
  // When secondary groups are present, groupedItems results from the server are sparse.
  // We seed empty page params for all possible group + secondary group combinations
  // to override any existing page params for combinations that do not exist in the
  // fresh server response.
  for (const secondaryGroup of secondaryGroups.nodes) {
    for (const group of response.groups.nodes) {
      const pageType = {
        groupId: group.groupId,
        secondaryGroupId: secondaryGroup.groupId,
      }
      const groupedItemsId = createGroupedItemsId(pageType)
      emptyPageParamsByGroupId[groupedItemsId] = []
    }
  }
  return emptyPageParamsByGroupId
}
